/**
 * JODestockageBanner — Featured banner on the home (Jardinerie universe)
 * listing destockage products with deepest discount first.
 *
 * Renders inside JardinsOleHomePage just before the testimonials section.
 * Self-hides if the admin disabled the banner OR if no products qualify.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Flame, ArrowRight, TrendingDown, AlertTriangle, Package } from "lucide-react";
import { useUniverse } from "@/lib/jardins-ole/useUniverse";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmt(c) {
  return ((c || 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export default function JODestockageBanner() {
  const { universe } = useUniverse();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/jardins-ole/shop/destockage`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (universe !== "jardinerie") return null;
  if (!data || !data.config?.banner_enabled) return null;
  const items = (data.items || []).slice(0, 8);
  if (items.length === 0) return null;

  const title = data.config.banner_title || "Déstockage en cours";
  const subtitle = data.config.banner_subtitle || "Stocks limités — n'attendez pas !";

  return (
    <section className="jo-destockage" data-testid="jo-destockage-banner">
      <div className="jo-container">
        <div className="jo-destockage-head">
          <span className="jo-destockage-pill">
            <Flame className="w-3.5 h-3.5" strokeWidth={2.4} />
            <span>Promotions</span>
          </span>
          <h2 className="jo-destockage-title">{title}</h2>
          <p className="jo-destockage-lead">{subtitle}</p>
        </div>

        <div className="jo-destockage-grid">
          {items.map((p) => {
            const imageUrl = p.featured_image
              ? (p.featured_image.startsWith("http") ? p.featured_image : `${BACKEND}${p.featured_image}`)
              : "";
            const lowStock = p.stock_tracking && p.stock_qty <= 5;
            return (
              <Link
                to={`/shop/${p.slug}`}
                key={p.id}
                className="jo-destockage-card"
                data-testid={`jo-destockage-item-${p.id}`}
              >
                <div className="jo-destockage-media">
                  {imageUrl ? (
                    <img src={imageUrl} alt={p.name} loading="lazy" />
                  ) : (
                    <Package className="w-12 h-12 text-white/40" />
                  )}
                  <span className="jo-destockage-badge">
                    <TrendingDown className="w-3 h-3" strokeWidth={2.4} />
                    -{p.discount_pct}%
                  </span>
                  {p.badge_text && (
                    <span className="jo-destockage-custom">{p.badge_text}</span>
                  )}
                  {lowStock && (
                    <span className="jo-destockage-lowstock">
                      <AlertTriangle className="w-3 h-3" strokeWidth={2.4} />
                      Plus que {p.stock_qty}
                    </span>
                  )}
                  {p.views_today >= 3 && (
                    <span className="jo-destockage-views" data-testid={`jo-destockage-views-${p.id}`}>
                      👁 {p.views_today} vue{p.views_today > 1 ? "s" : ""} aujourd'hui
                    </span>
                  )}
                </div>
                <div className="jo-destockage-body">
                  <h3 className="jo-destockage-name">{p.name}</h3>
                  <div className="jo-destockage-prices">
                    <span className="jo-destockage-price">{fmt(p.price_cents)}</span>
                    <span className="jo-destockage-compare">{fmt(p.compare_at_price_cents)}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="jo-destockage-footer">
          <Link to="/shop?destockage=1" className="jo-destockage-cta" data-testid="jo-destockage-see-all">
            Voir tous les produits en déstockage
            <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
          </Link>
        </div>
      </div>
    </section>
  );
}
